import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { DataCard } from "components/dataCard/dataCard";
import { formatCurrency } from "shared/utils/currency";
import {
  Card,
  CardHeader,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Spinner,
  Chip,
  Select,
  SelectItem,
  Accordion,
  AccordionItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Tooltip,
} from "@heroui/react";
import {
  Wrench,
  Calculator,
  Warning,
  Calendar,
  PaintBrush,
  Lightning,
  CheckCircle,
  ArrowUp,
  FileText,
} from "@phosphor-icons/react";
import {
  ProjectStatus,
  RenovationProject,
  DocumentType,
  RenovationLabelType,
  ProjectDateType,
  RenovationLabel,
} from "shared/types/renovation";
import { ResourceType } from "shared/types/resource";
import { toast } from "react-toastify";
import { format } from "date-fns";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import SearchComponent from "../searchBar/components/searchComponent";
import { LabelSearch } from "shared/components/labelSearch/labelSearch";
import { applyMask } from "shared/utils/masks";
import {
  documentLineAmount,
  sumDocumentLineAmounts,
} from "shared/utils/documentLineAmount";
import PlanningView from "./components/PlanningView";

interface RenovationMetrics {
  ongoingProjects: number;
  upcomingProjects: number;
  completedProjects: number;
  totalSpent: number;
  subsidies: {
    thisYear: number;
    allYears: number;
  };
}

interface Filters {
  year: number | "all" | string;
  buildingIds: string[];
  status?: ProjectStatus[];
  labelType?: RenovationLabelType;
  labelIds: number[];
  sortBy?: "startDate" | "invoicedAmount";
  sortDirection?: "asc" | "desc";
}

interface BuildingInvoiceImportMetric {
  buildingId: string;
  buildingName: string;
  invoiceCount: number;
  totalInvoiceAmount: number;
  supplierNamesDisplay?: string;
}

/**
 * Gefactureerd bedrag van één projectonderdeel.
 *
 * Dit scherm rekende dat op een tiental plaatsen zelf uit met
 * `Number(da.totalInclVat)`, en dat gaf drie keer een verkeerd cijfer:
 *
 * - **Het teken.** De importer bewaart factuurregels als *negatieve* bedragen
 *   (omgekeerd t.o.v. de Odoo-boekingsregel), de manuele upload niet. Een
 *   geïmporteerde factuur trok dus van het totaal áf.
 * - **Lege incl.-btw.** `totalInclVat` is nullable; regels met enkel
 *   `totalExclVat` telden voor 0, en een ontbrekend veld maakte de hele som
 *   `NaN` — dan stond er "€ NaN" of viel het cijfer op nul terug.
 * - **Creditnota's.** Die verlagen de kost, maar telden hier op.
 *
 * `documentLineAmount` past dezelfde regel toe als de backend-aggregaties en de
 * documentenlijst, zodat de cijfers op de drie plaatsen overeenkomen.
 */
/**
 * Balklabel voor bedragen op een projectonderdeel zonder hoofdcategorie.
 * Ze verdwenen vroeger in "Algemeen", waardoor die balk niet overeenkwam met de
 * kaart *Algemeen* erboven.
 */
const GEEN_HOOFDCATEGORIE = "Zonder hoofdcategorie";

const invoicedAmountForPart = (part: { documentLines?: any[] }): number =>
  sumDocumentLineAmounts(
    part?.documentLines?.filter(
      (line) => line?.document?.type === DocumentType.INVOICE,
    ),
  );

const calculateSpentAmount = (project: RenovationProject): number =>
  (project.parts ?? []).reduce(
    (sum, part) => sum + invoicedAmountForPart(part),
    0,
  );

// Helper functions to find actual/expected start/end
const getStartDate = (project: RenovationProject): Date | null => {
  const actualStart = project.dates?.find(
    (d) => d.type === ProjectDateType.ACTUAL_START && !d.isDeprecated,
  );
  const expectedStart = project.dates?.find(
    (d) => d.type === ProjectDateType.EXPECTED_START && !d.isDeprecated,
  );
  return actualStart
    ? new Date(actualStart.date)
    : expectedStart
      ? new Date(expectedStart.date)
      : null;
};

const getEndDate = (project: RenovationProject): Date | null => {
  const actualEnd = project.dates?.find(
    (d) => d.type === ProjectDateType.ACTUAL_END && !d.isDeprecated,
  );
  const expectedEnd = project.dates?.find(
    (d) => d.type === ProjectDateType.EXPECTED_END && !d.isDeprecated,
  );
  return actualEnd
    ? new Date(actualEnd.date)
    : expectedEnd
      ? new Date(expectedEnd.date)
      : null;
};

// Add this helper function
const getProjectYear = (project: RenovationProject): number => {
  // Look for the expected start date
  const startDateEntry = project.dates?.find(
    (date) =>
      date.type === ProjectDateType.EXPECTED_START && !date.isDeprecated,
  );

  if (startDateEntry && startDateEntry.date) {
    return new Date(startDateEntry.date).getFullYear();
  }

  // Fallback to creation date year
  return project.createdAt
    ? new Date(project.createdAt).getFullYear()
    : new Date().getFullYear();
};

// Cosmetic label mapping for project status
const getStatusDisplayLabel = (status: ProjectStatus): string => {
  switch (status) {
    case ProjectStatus.DESIGN:
      return "Ontwerp";
    case ProjectStatus.PLANNED:
      return "Uitvoeringsdossier";
    case ProjectStatus.IN_PROGRESS:
      return "In uitvoering - Werf";
    case ProjectStatus.COMPLETED:
      return "Voorlopige oplevering";
    case ProjectStatus.PAID:
      return "Definitieve oplevering (Volledig gefactureerd)";
    default:
      return status;
  }
};

const Renovation = () => {
  const { session } = useSession();
  const [invoiceImportMetrics, setInvoiceImportMetrics] = useState<
    BuildingInvoiceImportMetric[]
  >([]);

  const fetchInvoiceImportMetrics = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/invoice-import-metrics`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(
          `Failed to load invoice import metrics: ${response.status} ${response.statusText}`,
        );
      }
      const { data } = await response.json();
      setInvoiceImportMetrics(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching invoice import metrics:", error);
      setInvoiceImportMetrics([]);
    }
  };

  useEffect(() => {
    if (session) {
      fetchInvoiceImportMetrics();
    }
  }, [session]);

  const [metrics, setMetrics] = useState<RenovationMetrics>({
    ongoingProjects: 0,
    upcomingProjects: 0,
    completedProjects: 0,
    totalSpent: 0,
    subsidies: {
      thisYear: 0,
      allYears: 0,
    },
  });

  const [projects, setProjects] = useState<RenovationProject[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [filters, setFilters] = useState<Filters>({
    year: new Date().getFullYear(),
    buildingIds: [],
    labelIds: [],
  });

  const years = useMemo(() => {
    const nextYear = new Date().getFullYear() + 1;
    const withinTwoYears = new Date().getFullYear() + 2;
    const withinThreeYears = new Date().getFullYear() + 3;
    const currentYear = new Date().getFullYear();
    return [
      { key: "all", label: "Alle jaren" },
      { key: withinThreeYears, label: withinThreeYears.toString() },
      { key: withinTwoYears, label: withinTwoYears.toString() },
      { key: nextYear, label: nextYear.toString() },
      { key: `${currentYear}-future`, label: `${currentYear} verder verloop` },
      { key: currentYear, label: `${currentYear} verleden tot heden` },
      ...Array.from({ length: 4 }, (_, i) => ({
        key: currentYear - i - 1,
        label: (currentYear - i - 1).toString(),
      })),
    ];
  }, []);

  // Add state for labels and sorting
  const [labels, setLabels] = useState<RenovationLabel[]>([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);

  // Add this function to fetch labels
  const fetchLabels = async () => {
    setIsLoadingLabels(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/labels`,
        { method: "GET" },
        session,
      );
      if (!response.ok) throw new Error("Failed to fetch labels");
      const data = await response.json();
      setLabels(data);
    } catch (error) {
      console.error("Error fetching labels:", error);
      toast.error("Fout bij ophalen van labels");
    } finally {
      setIsLoadingLabels(false);
    }
  };

  // Update the filteredProjects memo
  const filteredProjects = useMemo(() => {
    let filtered = projects.filter((project) => {
      // Year filter
      if (filters.year !== "all") {
        const startDate = getStartDate(project);
        const endDate = getEndDate(project);
        const targetYear =
          typeof filters.year === "string" && filters.year.includes("-future")
            ? parseInt(filters.year.split("-")[0])
            : (filters.year as number);

        // For future planning view (verder verloop), include projects that are ongoing/active in that year
        if (
          typeof filters.year === "string" &&
          filters.year.includes("-future")
        ) {
          // For future planning, be more inclusive - include projects that might be active
          if (startDate && endDate) {
            // Both dates exist - check overlap
            const startYear = startDate.getFullYear();
            const endYear = endDate.getFullYear();
            if (!(startYear <= targetYear && endYear >= targetYear)) {
              return false;
            }
          } else if (startDate) {
            // Only start date - include if starts in or before target year
            const startYear = startDate.getFullYear();
            if (startYear > targetYear) {
              return false;
            }
          } else if (endDate) {
            // Only end date - include if ends in or after target year
            const endYear = endDate.getFullYear();
            if (endYear < targetYear) {
              return false;
            }
          }
          // If no dates at all, include it (might be planned for this year)
        } else {
          // For regular year view (verleden tot heden), include projects with ANY activity in that year
          if (startDate && endDate) {
            // Both dates exist - check overlap
            const startYear = startDate.getFullYear();
            const endYear = endDate.getFullYear();
            if (!(startYear <= targetYear && endYear >= targetYear)) {
              return false;
            }
          } else if (startDate) {
            // Only start date - include if starts in target year
            const startYear = startDate.getFullYear();
            if (startYear !== targetYear) {
              return false;
            }
          } else if (endDate) {
            // Only end date - include if ends in target year
            const endYear = endDate.getFullYear();
            if (endYear !== targetYear) {
              return false;
            }
          }
          // If no dates, fall back to creation year or project year
          else {
            const projectYear = getProjectYear(project);
            if (projectYear !== targetYear) {
              return false;
            }
          }
        }
      }

      // Status filter
      if (filters.status?.length) {
        if (!filters.status.includes(project.status)) {
          return false;
        }
      }

      // Label type filter
      if (filters.labelType) {
        const hasLabelType = project.parts?.some(
          (part) => part.label?.type === filters.labelType,
        );
        if (!hasLabelType) {
          return false;
        }
      }

      // Building filter
      if (filters.buildingIds.length > 0) {
        if (!filters.buildingIds.includes(project.building.id ?? "")) {
          return false;
        }
      }

      // Label filter - check if project has parts with ANY of the selected labels
      if (filters.labelIds.length > 0) {
        const hasAnyLabel = project.parts?.some((part) =>
          filters.labelIds.includes(part.label?.id ?? -1),
        );
        if (!hasAnyLabel) return false;
      }

      return true;
    });

    // Apply sorting
    if (filters.sortBy) {
      filtered = sortProjects(filtered, filters.sortBy, filters.sortDirection);
    }

    return filtered;
  }, [projects, filters]);

  // Loading state for filters
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // Add a ref for the table
  const tableRef = useRef<HTMLDivElement>(null);

  // Add scroll helper
  const scrollToTable = () => {
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch data with loading states
  const fetchData = async (newFilters: Filters) => {
    setIsFilterLoading(true);
    setFilters(newFilters);
    try {
      const queryParams = new URLSearchParams();
      if (newFilters.year !== "all") {
        queryParams.append("year", newFilters.year.toString());
      }
      if (newFilters.buildingIds.length > 0) {
        newFilters.buildingIds.forEach((id) =>
          queryParams.append("buildingIds[]", id),
        );
      }

      const isFutureYear =
        newFilters.year !== "all" &&
        (newFilters.year as number) > new Date().getFullYear();
      const projectsUrl = `${API_URL}/renovation/projects?${queryParams}${isFutureYear ? "&view=planning" : ""}`;
      const [projectsResponse, metricsResponse] = await Promise.all([
        fetchWithAuth(projectsUrl, { method: "GET" }, session),
        fetchWithAuth(
          `${API_URL}/renovation/metrics?${queryParams}`,
          { method: "GET" },
          session,
        ),
      ]);

      const [projectsData, metricsData] = await Promise.all([
        projectsResponse.json(),
        metricsResponse.json(),
      ]);

      setProjects(projectsData);
      setMetrics(metricsData);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Er ging iets mis bij het ophalen van de gegevens");
    } finally {
      setIsFilterLoading(false);
      setIsLoadingProjects(false);
    }
  };

  // Update the handleFilterChange function to properly handle status array
  const handleFilterChange = async (newFilters: Filters) => {
    setIsFilterLoading(true);
    // Clean up the filters before setting them
    const cleanedFilters = {
      ...newFilters,
      status: newFilters.status?.filter(Boolean), // Remove empty values
      labelIds: newFilters.labelIds?.filter((id) => id > 0), // Remove invalid IDs
      buildingIds: newFilters.buildingIds?.filter(Boolean), // Remove empty building IDs
    };

    // If arrays are empty, set them to undefined
    if (cleanedFilters.status?.length === 0) cleanedFilters.status = undefined;
    if (cleanedFilters.labelIds?.length === 0) cleanedFilters.labelIds = [];
    if (cleanedFilters.buildingIds?.length === 0)
      cleanedFilters.buildingIds = [];

    setFilters(cleanedFilters);
    try {
      const queryParams = new URLSearchParams();

      if (cleanedFilters.year !== "all") {
        queryParams.append("year", cleanedFilters.year.toString());
      }

      cleanedFilters.buildingIds.forEach((id) => {
        queryParams.append("buildingIds[]", id);
      });

      if (cleanedFilters.labelType) {
        queryParams.append("labelType", cleanedFilters.labelType);
      }

      // Handle status array
      if (cleanedFilters.status?.length) {
        cleanedFilters.status.forEach((status) => {
          queryParams.append("status[]", status);
        });
      }

      const isFutureYear =
        cleanedFilters.year !== "all" &&
        (cleanedFilters.year as number) > new Date().getFullYear();
      const projectsUrl = `${API_URL}/renovation/projects?${queryParams}${isFutureYear ? "&view=planning" : ""}`;
      // Fetch both projects and metrics with the same filters
      const [projRes, metricsRes] = await Promise.all([
        fetchWithAuth(projectsUrl, { method: "GET" }, session),
        fetchWithAuth(
          `${API_URL}/renovation/metrics?${queryParams}`,
          { method: "GET" },
          session,
        ),
      ]);

      const [projData, metricsData] = await Promise.all([
        projRes.json(),
        metricsRes.json(),
      ]);

      setProjects(projData);
      setMetrics(metricsData);
    } catch (error) {
      console.error("Filter fetch error:", error);
      toast.error("Fout bij laden van renovatiedata.");
    } finally {
      setIsFilterLoading(false);
    }
  };

  useEffect(() => {
    // Call fetchData immediately on mount to load the initial data.
    fetchData(filters);
  }, []); // <-- empty dependencies means run once on mount

  // Add useEffect to fetch labels on mount
  useEffect(() => {
    fetchLabels();
  }, []);

  // Add this sorting function
  const sortProjects = (
    projects: RenovationProject[],
    sortBy?: string,
    sortDirection?: "asc" | "desc",
  ) => {
    if (!sortBy) return projects;

    return [...projects].sort((a, b) => {
      if (sortBy === "startDate") {
        const aDate = getStartDate(a)?.getTime() || 0;
        const bDate = getStartDate(b)?.getTime() || 0;
        return sortDirection === "desc" ? bDate - aDate : aDate - bDate;
      }

      if (sortBy === "invoicedAmount") {
        const aAmount = calculateSpentAmount(a);
        const bAmount = calculateSpentAmount(b);
        return sortDirection === "desc" ? bAmount - aAmount : aAmount - bAmount;
      }

      return 0;
    });
  };

  // Update the renderProjectTable function
  const renderProjectTable = (projects: RenovationProject[]) => {
    // Sort the already filtered projects
    const sortedProjects = sortProjects(
      projects,
      filters.sortBy,
      filters.sortDirection,
    );

    return (
      <Table aria-label="Projects table">
        <TableHeader>
          <TableColumn>
            <button
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy: "startDate",
                  sortDirection:
                    prev.sortBy === "startDate" && prev.sortDirection === "desc"
                      ? "asc"
                      : "desc",
                }))
              }
              className="flex items-center gap-1"
            >
              Periode
              {filters.sortBy === "startDate" && (
                <ArrowUp
                  className={
                    filters.sortDirection === "desc" ? "rotate-180" : ""
                  }
                  size={14}
                />
              )}
            </button>
          </TableColumn>
          <TableColumn>Gebouw</TableColumn>
          <TableColumn>Units</TableColumn>
          <TableColumn>Project</TableColumn>
          <TableColumn>Hoofdcategorieën</TableColumn>
          <TableColumn>Status</TableColumn>
          <TableColumn>Onderdelen</TableColumn>
          <TableColumn>
            <button
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  sortBy: "invoicedAmount",
                  sortDirection:
                    prev.sortBy === "invoicedAmount" &&
                    prev.sortDirection === "desc"
                      ? "asc"
                      : "desc",
                }))
              }
              className="flex items-center gap-1"
            >
              Gefactureerd (incl. btw)
              {filters.sortBy === "invoicedAmount" && (
                <ArrowUp
                  className={
                    filters.sortDirection === "desc" ? "rotate-180" : ""
                  }
                  size={14}
                />
              )}
            </button>
          </TableColumn>
        </TableHeader>
        <TableBody>
          {sortedProjects.map((project) => {
            const startDate = getStartDate(project);
            const endDate = getEndDate(project);
            const invoicedAmount =
              project.parts?.reduce((total, part) => {
                const partInvoiced =
                  invoicedAmountForPart(part);
                return total + partInvoiced;
              }, 0) || 0;

            return (
              <TableRow key={project.id}>
                <TableCell>
                  {startDate ? format(startDate, "dd/MM/yyyy") : "-"}
                  {" → "}
                  {endDate ? format(endDate, "dd/MM/yyyy") : "-"}
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat" className="bg-neutral-100">
                    {project.building.id}
                  </Chip>
                </TableCell>
                <TableCell>
                  <div className="flex cursor-pointer flex-col gap-2">
                    <Accordion>
                      <AccordionItem
                        title={
                          <Chip
                            size="sm"
                            variant="flat"
                            className="cursor-pointer"
                          >
                            {project.units?.length || 0} units
                          </Chip>
                        }
                      >
                        <div className="mt-2 flex flex-wrap gap-1">
                          {project.building.units?.map((unit) => {
                            const isUsedInProject = project.units?.some(
                              (projectUnit) => projectUnit.id === unit.id,
                            );
                            return (
                              <Chip
                                key={unit.id}
                                size="sm"
                                className={
                                  isUsedInProject
                                    ? "bg-moss-primary text-white"
                                    : "border border-neutral-200 bg-white text-neutral-700"
                                }
                              >
                                {unit.id}
                                {unit.referencePlus &&
                                  ` (${unit.referencePlus})`}
                              </Chip>
                            );
                          })}
                        </div>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </TableCell>
                <TableCell>
                  <Link
                    to={`/renovation/${project.id}?buildingId=${project.building.id}`}
                    className="text-moss-primary hover:underline"
                  >
                    RENO-{project.id} {project.title}
                  </Link>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(project.parts ?? [])
                      .map((part) => part.label?.type)
                      .filter(
                        (value, i, self) => value && self.indexOf(value) === i,
                      )
                      .map((type) => (
                        <Chip
                          key={type}
                          size="sm"
                          className="bg-moss-primary/10 text-moss-primary"
                        >
                          {type}
                        </Chip>
                      ))}
                  </div>
                </TableCell>
                <TableCell>{project.status}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {(project.parts ?? []).map((part) => {
                      const partInvoicedAmount =
                        invoicedAmountForPart(part);

                      const isLabelFiltered = filters.labelIds.includes(
                        part.label?.id ?? -1,
                      );
                      const hasInvoicedAmount = partInvoicedAmount > 0;

                      return (
                        <div key={part.id} className="flex items-center gap-1">
                          <Chip
                            size="sm"
                            className={
                              isLabelFiltered
                                ? "bg-moss-primary cursor-pointer text-white"
                                : hasInvoicedAmount
                                  ? "bg-moss-primary/10 text-moss-primary border-moss-primary/20 cursor-pointer border"
                                  : "cursor-pointer border border-neutral-200 bg-white text-neutral-700"
                            }
                            onClick={() => {
                              setSelectedPart(part);
                              setEditPartModalOpen(true);
                            }}
                          >
                            <span className="font-medium">
                              {" "}
                              RENO-{project.id}-O{part.id}
                            </span>
                            <span className="mx-1">•</span>
                            <span className="font-medium">{part.title}</span>
                            {part.label && (
                              <>
                                <span className="mx-1">•</span>
                                <span>{part.label.name}</span>
                              </>
                            )}
                            <span className="mx-1">•</span>
                            <span>{formatCurrency(partInvoicedAmount)}</span>
                            <span>
                              {part.label?.type ? (
                                <Tooltip
                                  content={`Label ${part.label.type} geselecteerd`}
                                >
                                  <span
                                    className={`border-neutral-primary/30 bg-status-good ml-2 inline-block h-2 w-2 rounded-full border`}
                                  />
                                </Tooltip>
                              ) : (
                                <Tooltip
                                  content={`Klik hier om een label toe te wijzen`}
                                >
                                  <span
                                    className={`border-neutral-primary/30 bg-status-bad ml-2 inline-block h-2 w-2 rounded-full border`}
                                  />
                                </Tooltip>
                              )}
                            </span>
                          </Chip>
                        </div>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell>{formatCurrency(invoicedAmount)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  // Update the helper function to render filter chips
  /**
   * De actieve filters als chipjes boven een blok.
   *
   * `includeYear: false` voor blokken die het jaarfilter **niet** toepassen
   * (bv. "Algemene project statistieken — sinds oprichting Yally"). Daar stond
   * het jaarchipje er vroeger gewoon bij, waardoor het leek alsof het cijfer
   * over dat jaar ging. Dat is bug 521: "staat erbij dat de filter is toegepast,
   * maar ofwel werkt de filter niet, ofwel gaat het over bedragen door de jaren
   * heen".
   */
  const renderFilterChips = ({ includeYear = true } = {}) => {
    const chips = [];

    if (includeYear && filters.year !== "all") {
      chips.push(
        <Chip key="year" size="sm" variant="flat" className="bg-neutral-100">
          {filters.year}
        </Chip>,
      );
    }

    if (filters.buildingIds.length > 0) {
      filters.buildingIds.forEach((buildingId) => {
        chips.push(
          <Chip
            key={`building-${buildingId}`}
            size="sm"
            variant="flat"
            className="bg-neutral-100"
          >
            {buildingId}
          </Chip>,
        );
      });
    }

    if (filters.labelType) {
      chips.push(
        <Chip
          key="labelType"
          size="sm"
          variant="flat"
          className="bg-neutral-100"
        >
          {filters.labelType}
        </Chip>,
      );
    }

    if (filters.status?.length) {
      filters.status.forEach((status) => {
        chips.push(
          <Chip
            key={`status-${status}`}
            size="sm"
            variant="flat"
            className="bg-neutral-100"
          >
            {status}
          </Chip>,
        );
      });
    }

    if (filters.labelIds.length > 0) {
      filters.labelIds.forEach((labelId) => {
        const label = labels.find((l) => l.id === labelId);
        if (label) {
          chips.push(
            <Chip
              key={`label-${labelId}`}
              size="sm"
              variant="flat"
              className="bg-neutral-100"
            >
              {label.name}
            </Chip>,
          );
        }
      });
    }

    return chips.length > 0 ? (
      <div className="ml-2 flex items-center gap-2">
        <span className="text-sm text-neutral-500">Filters:</span>
        {chips}
      </div>
    ) : null;
  };

  /**
   * Valt dit projectonderdeel binnen de actieve filters?
   *
   * Eén regel voor het hele scherm. De grafieken hieronder ("Kosten per
   * subcategorie", "Gefactureerd per leverancier", …) toonden wél de
   * filterchipjes maar filterden zelf enkel op categorie: gebouw en jaar gingen
   * er straal aan voorbij. Dat is de tweede helft van bug 521 — "staat erbij dat
   * de filter is toegepast, maar ofwel werkt de filter niet, ofwel gaat het over
   * bedragen door de jaren heen".
   *
   * Het **jaar** is dat van het project (`getProjectYear`: verwachte startdatum,
   * anders de aanmaakdatum), niet de factuurdatum. Zo hoort een project van 2025
   * met een naloper-factuur in 2026 in zijn geheel bij 2025, en sluiten alle
   * blokken op dit scherm op elkaar aan.
   */
  const partMatchesFilters = useCallback(
    (project: RenovationProject, part: any): boolean => {
      if (
        filters.buildingIds.length > 0 &&
        !filters.buildingIds.includes((project.building?.id as string) || "")
      ) {
        return false;
      }
      if (filters.year !== "all" && filters.year) {
        if (getProjectYear(project) !== parseInt(filters.year as any)) {
          return false;
        }
      }
      if (filters.labelType && part?.label?.type !== filters.labelType) {
        return false;
      }
      if (
        filters.labelIds.length > 0 &&
        !filters.labelIds.includes(part?.label?.id)
      ) {
        return false;
      }
      return true;
    },
    [filters.buildingIds, filters.year, filters.labelType, filters.labelIds],
  );

  // Move the useMemo hooks directly to component level, not inside renderMetricsSection
  // Calculate filtered metrics for the overall project stats
  const filteredTotalMetrics = useMemo(() => {
    // Gebouw- en labelfilter gelden hier altijd; het **jaarfilter** wordt apart
    // bijgehouden.
    //
    // "Totaal uitgegeven" telde alle jaren op terwijl de kop *Financiële
    // overzicht 2026* zei en het jaarchipje er gewoon bij stond (bug 521:
    // "totaal uitgegeven = sinds de opstart van Yally, filter staat nochtans op
    // 2025"). Het cijfer was niet fout, het antwoordde op een andere vraag dan
    // de kop stelde. Nu worden **beide** getoond, en het jaarcijfer gebruikt
    // dezelfde jaarregel als *Kosten per hoofdcategorie* (het jaar van het
    // project, `getProjectYear`), zodat die twee blokken op elkaar aansluiten.
    const totalMetrics = {
      totalProjects: 0,
      totalBudget: 0,
      totalInvoiced: 0,
      totalInvoicedInYear: 0,
    };

    projects.forEach((project) => {
      // Skip projects that don't match building filter
      if (
        filters.buildingIds.length > 0 &&
        !filters.buildingIds.includes((project.building.id as string) || "")
      )
        return;

      let includeProject = true;
      let projectTotalInvoiced = 0;

      // Check if any part matches the label filters
      if (filters.labelType || filters.labelIds.length > 0) {
        includeProject = false; // Reset flag, will set to true if any part matches

        project.parts?.forEach((part) => {
          if (!part.label) return;

          // Check label type filter
          if (filters.labelType && part.label.type !== filters.labelType)
            return;

          // Check label ID filter
          if (
            filters.labelIds.length > 0 &&
            !filters.labelIds.includes(part.label.id)
          )
            return;

          // If we reach here, at least one part matches the filters
          includeProject = true;

          projectTotalInvoiced += invoicedAmountForPart(part);
        });
      } else {
        // No label filters, calculate total invoiced for all parts
        projectTotalInvoiced =
          project.parts?.reduce(
            (sum, part) => sum + invoicedAmountForPart(part),
            0,
          ) ||
          0;
      }

      if (includeProject) {
        totalMetrics.totalProjects++;
        totalMetrics.totalInvoiced += projectTotalInvoiced;

        const hoortBijGekozenJaar =
          filters.year === "all" ||
          !filters.year ||
          getProjectYear(project) === parseInt(filters.year as any);
        if (hoortBijGekozenJaar) {
          totalMetrics.totalInvoicedInYear += projectTotalInvoiced;
        }

        // Calculate budget based on parts that match filters
        const projectBudget =
          project.parts?.reduce((sum, part) => {
            // Skip parts that don't match label filters
            if (
              filters.labelType &&
              (!part.label || part.label.type !== filters.labelType)
            )
              return sum;
            if (
              filters.labelIds.length > 0 &&
              (!part.label || !filters.labelIds.includes(part.label.id))
            )
              return sum;

            // Budget now comes from investment item assignments
            const partBudget =
              part.investmentAssignments?.reduce(
                (partSum: number, assignment: any) =>
                  partSum + Number(assignment.assignedAmount),
                0,
              ) || 0;
            return sum + partBudget;
          }, 0) || 0;

        totalMetrics.totalBudget += projectBudget;
      }
    });

    return totalMetrics;
  }, [
    projects,
    filters.buildingIds,
    filters.labelType,
    filters.labelIds,
    filters.year,
  ]);

  // Now renderMetricsSection is just a regular render function without hooks
  const renderMetricsSection = () => {
    // Get the display year (current year if 'all' is selected)
    const displayYear =
      filters.year === "all" ? new Date().getFullYear() : filters.year;

    return (
      <div className="flex flex-col gap-4">
        {/* Project metrics - only shown when viewing all years or with clarification */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">
              Algemene project statistieken
            </h3>
            <span className="text-sm text-neutral-500">
              (sinds oprichting Yally)
            </span>
            {/* Dit blok telt alle jaren op, dus geen jaarchipje. */}
            {renderFilterChips({ includeYear: false })}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div
              onClick={() => {
                handleFilterChange({
                  ...filters,
                  status: [ProjectStatus.IN_PROGRESS, ProjectStatus.COMPLETED],
                });
                scrollToTable();
              }}
              className="cursor-pointer"
            >
              <DataCard
                title={metrics.ongoingProjects.toString()}
                subtitle="Lopende projecten"
                icon={<Wrench />}
              />
            </div>
            <div
              onClick={() => {
                handleFilterChange({
                  ...filters,
                  status: [ProjectStatus.PLANNED, ProjectStatus.DESIGN],
                });
                scrollToTable();
              }}
              className="cursor-pointer"
            >
              <DataCard
                title={metrics.upcomingProjects.toString()}
                subtitle="Aankomende projecten"
                icon={<Calendar />}
              />
            </div>
            <div
              onClick={() => {
                handleFilterChange({
                  ...filters,
                  status: [ProjectStatus.PAID],
                });
                scrollToTable();
              }}
              className="cursor-pointer"
            >
              <DataCard
                title={metrics.completedProjects.toString()}
                subtitle="Volledig gefactureerde projecten"
                icon={<CheckCircle />}
              />
            </div>
            <Link to="/renovation/documents" className="cursor-pointer">
              <DataCard
                title={(invoiceImportMetrics?.length || 0).toString()}
                subtitle="Gebouwen met te importeren facturen"
                icon={<FileText />}
              />
            </Link>
          </div>
        </div>

        {/* Financial metrics - specific to the selected year */}
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">
              Financiële overzicht {displayYear}
            </h3>
            {renderFilterChips()} {/* Include all filters, including year */}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* Twee cijfers op één kaart: het gekozen jaar groot, "sinds
                opstart" eronder. Vroeger stond hier enkel dat tweede, met de
                kop en het jaarchipje van het eerste erboven. */}
            <DataCard
              title={applyMask(
                filters.year === "all"
                  ? filteredTotalMetrics.totalInvoiced
                  : filteredTotalMetrics.totalInvoicedInYear,
                "valuta",
              )}
              subtitle={
                filters.year === "all"
                  ? "Totaal uitgegeven (alle jaren)"
                  : `Totaal uitgegeven in ${displayYear}`
              }
              label={
                filters.year === "all"
                  ? undefined
                  : `Sinds opstart Yally: ${applyMask(filteredTotalMetrics.totalInvoiced, "valuta")}`
              }
              icon={<Calculator />}
            />
            <DataCard
              title={applyMask(metrics.subsidies.thisYear, "valuta")}
              subtitle={`Subsidies verkregen in ${displayYear}`}
              label={`Totaal alle jaren: ${applyMask(metrics.subsidies.allYears, "valuta")}`}
              icon={<ArrowUp />}
            />
          </div>
        </div>
      </div>
    );
  };

  // Add the category metrics section
  const renderCategoryMetrics = () => {
    // Calculate category metrics based on filtered projects
    const filteredCategoryMetrics = {
      [RenovationLabelType.SAFETY]: 0,
      [RenovationLabelType.ENERGETIC]: 0,
      [RenovationLabelType.ESTHETIC]: 0,
      [RenovationLabelType.GENERAL]: 0,
    };

    // Wat er buiten de vier categorieën valt: een projectonderdeel zonder
    // categorie, of met een categorie waar geen hoofdcategorie (`type`) aan
    // hangt. Die bedragen verdwenen vroeger stil uit dit blok, en dan klopt de
    // optelsom van de vier kaarten niet met "Totaal uitgegeven" — precies de
    // vraag uit bug 521 ("Capex staat op 676K, de optelling van de 4 labels is
    // veel lager, nochtans zijn alle labels aangevuld").
    let zonderCategorie = 0;

    // Apply filters to calculate category metrics
    projects.forEach((project) => {
      project.parts?.forEach((part) => {
        if (!partMatchesFilters(project, part)) return;

        // Get invoiced amount for the part
        const invoicedAmount =
          invoicedAmountForPart(part);
        if (invoicedAmount === 0) return;

        if (part.label?.type) {
          filteredCategoryMetrics[part.label.type] += invoicedAmount;
        } else {
          // Geen categorie of geen hoofdcategorie: apart bijhouden i.p.v. laten
          // vallen. Enkel wanneer er niet op categorie gefilterd wordt — met een
          // categoriefilter aan is "zonder categorie" per definitie leeg.
          if (!filters.labelType && filters.labelIds.length === 0) {
            zonderCategorie += invoicedAmount;
          }
        }
      });
    });

    const totalSpent = Object.values(filteredCategoryMetrics).reduce(
      (a, b) => a + b,
      0,
    );

    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium">Kosten per hoofdcategorie</h3>
          {renderFilterChips()}
        </div>
        {/* Zeggen waaruit de optelsom bestaat. Zonder deze regel is het gissen
            waarom vier kaarten samen niet op "Totaal uitgegeven" uitkomen. */}
        <p className="text-sm text-neutral-500">
          Samen {formatCurrency(totalSpent + zonderCategorie)}
          {filters.year !== "all" && ` in ${filters.year}`}
          {zonderCategorie > 0 && (
            <>
              , waarvan{" "}
              <span className="font-medium text-amber-700">
                {formatCurrency(zonderCategorie)} op onderdelen zonder
                hoofdcategorie
              </span>{" "}
              — die staan in geen van de vier kaarten hieronder
            </>
          )}
          .
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div
            onClick={() => {
              handleFilterChange({
                ...filters,
                labelType: RenovationLabelType.SAFETY,
              });
              scrollToTable();
            }}
            className="cursor-pointer"
          >
            <DataCard
              title={formatCurrency(
                filteredCategoryMetrics[RenovationLabelType.SAFETY],
              )}
              subtitle="Veiligheid"
              percentage={
                totalSpent
                  ? (filteredCategoryMetrics[RenovationLabelType.SAFETY] /
                      totalSpent) *
                    100
                  : 0
              }
              icon={<Warning />}
            />
          </div>
          <div
            onClick={() => {
              handleFilterChange({
                ...filters,
                labelType: RenovationLabelType.ENERGETIC,
              });
              scrollToTable();
            }}
            className="cursor-pointer"
          >
            <DataCard
              title={formatCurrency(
                filteredCategoryMetrics[RenovationLabelType.ENERGETIC],
              )}
              subtitle="Energetisch"
              percentage={
                totalSpent
                  ? (filteredCategoryMetrics[RenovationLabelType.ENERGETIC] /
                      totalSpent) *
                    100
                  : 0
              }
              icon={<Lightning />}
            />
          </div>
          <div
            onClick={() => {
              handleFilterChange({
                ...filters,
                labelType: RenovationLabelType.ESTHETIC,
              });
              scrollToTable();
            }}
            className="cursor-pointer"
          >
            <DataCard
              title={formatCurrency(
                filteredCategoryMetrics[RenovationLabelType.ESTHETIC],
              )}
              subtitle="Esthetisch"
              percentage={
                totalSpent
                  ? (filteredCategoryMetrics[RenovationLabelType.ESTHETIC] /
                      totalSpent) *
                    100
                  : 0
              }
              icon={<PaintBrush />}
            />
          </div>
          <div
            onClick={() => {
              handleFilterChange({
                ...filters,
                labelType: RenovationLabelType.GENERAL,
              });
              scrollToTable();
            }}
            className="cursor-pointer"
          >
            <DataCard
              title={formatCurrency(
                filteredCategoryMetrics[RenovationLabelType.GENERAL],
              )}
              subtitle="Algemeen"
              percentage={
                totalSpent
                  ? (filteredCategoryMetrics[RenovationLabelType.GENERAL] /
                      totalSpent) *
                    100
                  : 0
              }
              icon={<Wrench />}
            />
          </div>
        </div>
      </div>
    );
  };

  // Register ChartJS components
  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    ArcElement,
  );

  // Update the LabelCharts component
  const LabelCharts = ({ projects }: { projects: RenovationProject[] }) => {
    const { typeChartData, nameChartData } = useMemo(() => {
      // Initialize data structures
      const typeAmounts: { [key: string]: number } = {
        [RenovationLabelType.SAFETY]: 0,
        [RenovationLabelType.ENERGETIC]: 0,
        [RenovationLabelType.ESTHETIC]: 0,
        [RenovationLabelType.GENERAL]: 0,
      };

      const nameAmounts: { [key: string]: number } = {};

      // Calculate amounts for each project part
      projects.forEach((project) => {
        project.parts?.forEach((part) => {
          // Zelfde filterregel als de rest van het scherm — deze grafieken
          // negeerden gebouw en jaar, terwijl de chipjes erboven het
          // tegenovergestelde beweerden.
          if (!partMatchesFilters(project, part)) return;

          // Get invoiced amount for the part
          const invoicedAmount =
            invoicedAmountForPart(part);

          // If no label filters are active, include all invoiced parts
          if (filters.labelIds.length === 0) {
            if (part.label?.type) {
              typeAmounts[part.label.type] += invoicedAmount;
            } else {
              // Zonder hoofdcategorie: een eigen balk, niet stil bij "Algemeen".
              // Anders toont deze grafiek een ander bedrag voor Algemeen dan de
              // kaart erboven.
              typeAmounts[GEEN_HOOFDCATEGORIE] =
                (typeAmounts[GEEN_HOOFDCATEGORIE] || 0) + invoicedAmount;
            }

            if (part.label?.name) {
              nameAmounts[part.label.name] =
                (nameAmounts[part.label.name] || 0) + invoicedAmount;
            } else if (invoicedAmount > 0) {
              // If no label but has invoice, add to "Geen label"
              nameAmounts["Geen label"] =
                (nameAmounts["Geen label"] || 0) + invoicedAmount;
            }
          }
          // If label filters are active, only include parts with matching labels
          else if (part.label && filters.labelIds.includes(part.label.id)) {
            if (part.label.type) {
              typeAmounts[part.label.type] += invoicedAmount;
            }
            if (part.label.name) {
              nameAmounts[part.label.name] =
                (nameAmounts[part.label.name] || 0) + invoicedAmount;
            }
          }
        });
      });

      // Sort name amounts by amount (descending)
      const sortedNameAmounts = Object.entries(nameAmounts)
        .sort((a, b) => b[1] - a[1])
        .reduce(
          (obj, [key, value]) => ({
            ...obj,
            [key]: value,
          }),
          {},
        );

      return {
        typeChartData: {
          labels: Object.keys(typeAmounts),
          datasets: [
            {
              data: Object.values(typeAmounts),
              backgroundColor: [
                "rgba(234, 129, 98, 0.8)", // WARM ROOD #EA8162
                "rgba(91, 115, 4, 0.8)", // OLIJFGROEN #5B7304
                "rgba(245, 221, 127, 0.8)", // ZACHT GEEL #F5DD7F
                "rgba(49, 64, 59, 0.7)", // GRAFIET #31403B
              ],
              borderColor: [
                "rgba(234, 129, 98, 1)", // WARM ROOD #EA8162
                "rgba(91, 115, 4, 1)", // OLIJFGROEN #5B7304
                "rgba(245, 221, 127, 1)", // ZACHT GEEL #F5DD7F
                "rgba(49, 64, 59, 1)", // GRAFIET #31403B
              ],
              borderWidth: 1,
            },
          ],
        },
        nameChartData: {
          labels: Object.keys(sortedNameAmounts),
          datasets: [
            {
              data: Object.values(sortedNameAmounts),
              backgroundColor: "rgba(91, 115, 4, 0.8)", // OLIJFGROEN #5B7304
              borderColor: "rgba(91, 115, 4, 1)", // OLIJFGROEN #5B7304
              borderWidth: 1,
            },
          ],
        },
      };
    }, [projects, filters.labelIds, partMatchesFilters]);

    // Update the chart options to match the Yally style
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) => formatCurrency(Number(value)),
            color: "#31403B", // GRAFIET
          },
          grid: {
            color: "rgba(49, 64, 59, 0.1)", // GRAFIET with low opacity
          },
        },
        x: {
          ticks: {
            color: "#31403B", // GRAFIET
          },
          grid: {
            color: "rgba(49, 64, 59, 0.1)", // GRAFIET with low opacity
          },
        },
      },
    };

    return (
      <div className="relative z-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-4">
          <CardHeader>
            <div className="flex items-center gap-2">
              <h3 className="text-neutral-primary text-lg font-medium">
                Kosten per subcategorie
              </h3>
              {renderFilterChips()}
            </div>
          </CardHeader>
          <div className="h-[400px]">
            <Bar data={typeChartData} options={chartOptions} />
          </div>
        </Card>

        <Card className="p-4">
          <CardHeader>
            <h3 className="text-neutral-primary text-lg font-medium">
              Gefactureerd per subcategorie
            </h3>
            {renderFilterChips()}
          </CardHeader>
          <div className="h-[300px]">
            <Bar data={nameChartData} options={chartOptions} />
          </div>
        </Card>
      </div>
    );
  };

  // Update the SupplierChart component
  const SupplierChart = ({ projects }: { projects: RenovationProject[] }) => {
    const supplierChartData = useMemo(() => {
      // Create a data structure for each supplier with filtered parts
      const supplierData: {
        [supplierId: string]: {
          name: string;
          total: number;
          buildings: {
            [buildingId: string]: {
              amount: number;
              parts: { title: string; amount: number }[];
            };
          };
        };
      } = {};

      // First, collect all suppliers from parts that match the filters
      projects.forEach((project) => {
        const buildingId = project.building.id;

        project.parts?.forEach((part) => {
          // Zelfde filterregel als de rest van het scherm: gebouw en jaar telden
          // hier vroeger niet mee, terwijl de chipjes erboven het wel beweerden.
          if (!partMatchesFilters(project, part)) return;

          // Proceed with filtered parts only
          part.documentLines?.forEach((line) => {
            if (!line.document.supplier) return;
            if (line.document.type !== DocumentType.INVOICE) return;

            const supplierId = line.document.supplier.id.toString();
            const supplierName = line.document.supplier.name;
            // Negatief bij een creditnota: die verlaagt wat deze leverancier
            // gekost heeft. De vroegere `<= 0`-controle sloeg creditnota's over,
            // waardoor een leverancier die er een gestuurd had te duur bleef
            // staan in de top 10. Nul overslaan blijft wel — dat scheelt lege
            // rijen in de opbouw.
            const amount = documentLineAmount(line);
            if (amount === 0) return;

            // Initialize supplier if not exists
            if (!supplierData[supplierId]) {
              supplierData[supplierId] = {
                name: supplierName,
                total: 0,
                buildings: {},
              };
            }

            // Add to supplier total
            supplierData[supplierId].total += amount;

            // Initialize building if not exists
            if (!supplierData[supplierId].buildings[buildingId as string]) {
              supplierData[supplierId].buildings[buildingId as string] = {
                amount: 0,
                parts: [],
              };
            }

            // Add to building within supplier
            supplierData[supplierId].buildings[buildingId as string].amount +=
              amount;

            // Track part details
            const partExists = supplierData[supplierId].buildings[
              buildingId as string
            ].parts.some((p) => p.title === part.title);

            if (!partExists) {
              supplierData[supplierId].buildings[
                buildingId as string
              ].parts.push({
                title: part.title,
                amount: amount,
              });
            } else {
              // Update existing part amount
              supplierData[supplierId].buildings[buildingId as string].parts =
                supplierData[supplierId].buildings[
                  buildingId as string
                ].parts.map((p) => {
                  if (p.title === part.title) {
                    return { ...p, amount: p.amount + amount };
                  }
                  return p;
                });
            }
          });
        });
      });

      // Sort suppliers by total amount (descending)
      const sortedSuppliers = Object.entries(supplierData)
        // Een leverancier die per saldo op nul of minder uitkomt (enkel een
        // creditnota in beeld) hoort niet in een grafiek van wat er uitgegeven
        // is; die zou er als een balk van niets of een negatieve balk staan.
        .filter(([, supplier]) => supplier.total > 0)
        .sort(([, a], [, b]) => b.total - a.total)
        .slice(0, 10); // Limit to top 10 suppliers

      return {
        labels: sortedSuppliers.map(([, supplier]) => supplier.name),
        datasets: [
          {
            data: sortedSuppliers.map(([, supplier]) => supplier.total),
            backgroundColor: sortedSuppliers.map((_, index) => {
              // Use consistent Yally brand colors with different opacities
              const colors = [
                "rgba(91, 115, 4, 0.9)", // OLIJFGROEN #5B7304
                "rgba(234, 129, 98, 0.9)", // WARM ROOD #EA8162
                "rgba(245, 221, 127, 0.9)", // ZACHT GEEL #F5DD7F
                "rgba(184, 237, 217, 0.9)", // MUNTGROEN #B8EDD9
                "rgba(40, 63, 17, 0.9)", // DONKERGROEN #283F11
                "rgba(91, 115, 4, 0.7)", // OLIJFGROEN (lighter)
                "rgba(234, 129, 98, 0.7)", // WARM ROOD (lighter)
                "rgba(245, 221, 127, 0.7)", // ZACHT GEEL (lighter)
                "rgba(184, 237, 217, 0.7)", // MUNTGROEN (lighter)
                "rgba(49, 64, 59, 0.7)", // GRAFIET #31403B
              ];
              return colors[index % colors.length];
            }),
            buildingDetails: sortedSuppliers.map(
              ([, supplier]) => supplier.buildings,
            ),
          },
        ],
      };
    }, [projects, filters.labelType, filters.labelIds, partMatchesFilters]);

    const options = {
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const buildingDetails =
                context.dataset.buildingDetails[context.dataIndex];
              const labels = [`Totaal: ${formatCurrency(context.raw)}`];

              // Add building details
              Object.entries(buildingDetails).forEach(
                ([buildingId, data]: [string, any]) => {
                  labels.push(`${buildingId}: ${formatCurrency(data.amount)}`);
                  data.parts.forEach((part: any) => {
                    labels.push(
                      `  - ${part.title}: ${formatCurrency(part.amount)}`,
                    );
                  });
                },
              );

              return labels;
            },
          },
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          ticks: {
            callback: (value: number) => formatCurrency(value),
            color: "#31403B", // GRAFIET
          },
          grid: {
            color: "rgba(49, 64, 59, 0.1)", // GRAFIET with low opacity
          },
        },
        y: {
          stacked: true,
          ticks: {
            color: "#31403B", // GRAFIET
          },
          grid: {
            color: "rgba(49, 64, 59, 0.1)", // GRAFIET with low opacity
          },
        },
      },
    };

    return (
      <Card className="p-4">
        <CardHeader>
          <div className="flex items-center gap-2">
            <h3 className="text-neutral-primary text-lg font-medium">
              Gefactureerd per leverancier
            </h3>
            {renderFilterChips()}
          </div>
        </CardHeader>
        <div className="h-[400px]">
          <Bar data={supplierChartData} options={options as any} />
        </div>
      </Card>
    );
  };

  // Add state for the modal and selected part
  const [editPartModalOpen, setEditPartModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState<any>(null);
  const [isUpdatingPart, setIsUpdatingPart] = useState(false);

  // Move chart data calculations inside the BuildingChart component
  const BuildingChart = ({ projects }: { projects: RenovationProject[] }) => {
    // Chart view toggle state
    const [chartView, setChartView] = useState<
      "byBuilding" | "byCategory" | "bySubcategory"
    >("byBuilding");

    // Building-centric view data (calculated from filtered projects)
    const buildingChartData = useMemo(() => {
      // Initialize with proper structure including projects
      const buildingData: {
        [buildingId: string]: {
          total: number;
          categories: {
            [category: string]: number;
          };
          projects: {
            [projectId: string]: {
              title: string;
              amount: number;
              categories: { [category: string]: number };
            };
          };
        };
      } = {};

      // Process each project from filtered list
      projects.forEach((project) => {
        const buildingId = project.building.id as string; // Add type assertion

        // Skip if no parts
        if (!project.parts || project.parts.length === 0) return;

        // Initialize building if not exists
        if (!buildingData[buildingId]) {
          buildingData[buildingId] = {
            total: 0,
            categories: {},
            projects: {},
          };

          // Initialize categories
          Object.values(RenovationLabelType).forEach((category) => {
            buildingData[buildingId].categories[category] = 0;
          });
        }

        // Initialize project if not exists
        if (!buildingData[buildingId].projects[project.id]) {
          buildingData[buildingId].projects[project.id] = {
            title: project.title || `RENO-${project.id}`,
            amount: 0,
            categories: {},
          };
        }

        // Process each part in the project - ONLY include parts that match the label filters
        project.parts.forEach((part) => {
          // If label type filter is applied, skip parts that don't match
          if (filters.labelType && part.label?.type !== filters.labelType)
            return;

          // If label ID filter is applied, skip parts that don't match
          if (
            filters.labelIds.length > 0 &&
            (!part.label || !filters.labelIds.includes(part.label.id))
          )
            return;

          // Get invoiced amount for the part
          const invoicedAmount =
            invoicedAmountForPart(part);

          if (invoicedAmount <= 0) return;

          // Get category (default to GENERAL if not specified)
          const category = part.label?.type || RenovationLabelType.GENERAL;

          // Add to building total
          buildingData[buildingId].total += invoicedAmount;

          // Add to project amount
          buildingData[buildingId].projects[project.id].amount +=
            invoicedAmount;

          // Initialize category in project if not exists
          if (
            !buildingData[buildingId].projects[project.id].categories[category]
          ) {
            buildingData[buildingId].projects[project.id].categories[category] =
              0;
          }

          // Add to category within building
          if (!buildingData[buildingId].categories[category]) {
            buildingData[buildingId].categories[category] = 0;
          }
          buildingData[buildingId].categories[category] += invoicedAmount;

          // Add to category within project
          buildingData[buildingId].projects[project.id].categories[category] +=
            invoicedAmount;
        });

        // After processing all parts, remove buildings with zero totals
        // (this can happen if all parts were filtered out by label filters)
        if (buildingData[buildingId].total === 0) {
          delete buildingData[buildingId];
        }
      });

      // Sort buildings by total amount (descending)
      const sortedBuildings = Object.entries(buildingData)
        .sort(([, a], [, b]) => b.total - a.total)
        .slice(0, 15); // Limit to top 15 buildings to keep chart readable

      const categoryKeys = Object.values(RenovationLabelType);

      // Prepare datasets - one for each category with Yally brand colors
      const datasets = categoryKeys.map((category) => ({
        label: category,
        data: sortedBuildings.map(([, data]) => data.categories[category] || 0), // Remove unused buildingId
        backgroundColor:
          category === RenovationLabelType.SAFETY
            ? "rgba(234, 129, 98, 0.8)" // WARM ROOD #EA8162
            : category === RenovationLabelType.ENERGETIC
              ? "rgba(91, 115, 4, 0.8)" // OLIJFGROEN #5B7304
              : category === RenovationLabelType.ESTHETIC
                ? "rgba(245, 221, 127, 0.8)" // ZACHT GEEL #F5DD7F
                : "rgba(49, 64, 59, 0.7)", // GRAFIET #31403B
        borderColor:
          category === RenovationLabelType.SAFETY
            ? "rgba(234, 129, 98, 1)" // WARM ROOD #EA8162
            : category === RenovationLabelType.ENERGETIC
              ? "rgba(91, 115, 4, 1)" // OLIJFGROEN #5B7304
              : category === RenovationLabelType.ESTHETIC
                ? "rgba(245, 221, 127, 1)" // ZACHT GEEL #F5DD7F
                : "rgba(49, 64, 59, 1)", // GRAFIET #31403B
        borderWidth: 1,
        buildingProjects: sortedBuildings.map(([, data]) => {
          // Remove unused buildingId
          // Get projects for this building and category
          return Object.entries(data.projects)
            .filter(([, project]) => project.categories[category] > 0)
            .sort(
              (a, b) => b[1].categories[category] - a[1].categories[category],
            )
            .map(([projectId, project]) => ({
              id: projectId,
              title: project.title,
              amount: project.categories[category],
            }));
        }),
      }));

      return {
        labels: sortedBuildings.map(([buildingId]) => buildingId),
        datasets,
      };
    }, [projects, filters]);

    // Category-centric view data (calculated from filtered projects)
    const categoryChartData = useMemo(() => {
      // Initialize data structure for categories
      const categoryData: {
        [category: string]: {
          total: number;
          buildings: {
            [buildingId: string]: number;
          };
        };
      } = {};

      // Initialize categories
      Object.values(RenovationLabelType).forEach((category) => {
        categoryData[category] = {
          total: 0,
          buildings: {},
        };
      });

      // Calculate amounts for each category
      projects.forEach((project) => {
        const buildingId = project.building.id;

        project.parts?.forEach((part) => {
          // If label type filter is applied, skip parts that don't match
          if (filters.labelType && part.label?.type !== filters.labelType)
            return;

          // If label ID filter is applied, skip parts that don't match
          if (
            filters.labelIds.length > 0 &&
            (!part.label || !filters.labelIds.includes(part.label.id))
          )
            return;

          const invoicedAmount =
            invoicedAmountForPart(part);

          if (invoicedAmount <= 0) return;

          // Get category (default to GENERAL if not specified)
          const category = part.label?.type || RenovationLabelType.GENERAL;

          // Initialize building in category if not exists
          if (!categoryData[category].buildings[buildingId as string]) {
            categoryData[category].buildings[buildingId as string] = 0;
          }

          // Add to category total
          categoryData[category].total += invoicedAmount;

          // Add to building amount within this category
          categoryData[category].buildings[buildingId as string] +=
            invoicedAmount;
        });
      });

      // Sort categories by total amount (descending)
      const sortedCategories = Object.entries(categoryData).sort(
        ([, a], [, b]) => b.total - a.total,
      );

      // Get top 10 buildings overall by total spent
      const buildingTotals: { [buildingId: string]: number } = {};

      Object.values(categoryData).forEach((category) => {
        Object.entries(category.buildings).forEach(([buildingId, amount]) => {
          if (!buildingTotals[buildingId]) buildingTotals[buildingId] = 0;
          buildingTotals[buildingId] += amount;
        });
      });

      const topBuildings = Object.entries(buildingTotals)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10)
        .map(([buildingId]) => buildingId);

      // Create datasets - one per building
      const datasets = topBuildings.map((buildingId) => {
        // Prepare project details for tooltips
        const categoryProjects = sortedCategories.map(([category]) => {
          // Collect projects for this building and category
          const buildingCategoryProjects = projects
            .filter((project) => project.building.id === buildingId)
            .map((project) => {
              // Calculate amount from parts matching this category
              const amount =
                project.parts?.reduce((sum, part) => {
                  if (part.label?.type !== category) return sum;

                  const partAmount =
                    invoicedAmountForPart(part);

                  return sum + partAmount;
                }, 0) || 0;

              return {
                id: project.id,
                title: project.title || `RENO-${project.id}`,
                amount,
              };
            })
            .filter((p) => p.amount > 0)
            .sort((a, b) => b.amount - a.amount);

          return buildingCategoryProjects;
        });

        return {
          label: buildingId,
          data: sortedCategories.map(
            ([category]) => categoryData[category].buildings[buildingId] || 0,
          ),
          backgroundColor:
            buildingId === topBuildings[0]
              ? "rgba(234, 129, 98, 0.8)" // WARM ROOD #EA8162
              : buildingId === topBuildings[1]
                ? "rgba(91, 115, 4, 0.8)" // OLIJFGROEN #5B7304
                : buildingId === topBuildings[2]
                  ? "rgba(245, 221, 127, 0.8)" // ZACHT GEEL #F5DD7F
                  : buildingId === topBuildings[3]
                    ? "rgba(184, 237, 217, 0.8)" // MUNTGROEN #B8EDD9
                    : buildingId === topBuildings[4]
                      ? "rgba(40, 63, 17, 0.8)" // DONKERGROEN #283F11
                      : "rgba(49, 64, 59, 0.7)", // GRAFIET #31403B with decreasing opacity
          borderColor:
            buildingId === topBuildings[0]
              ? "rgba(234, 129, 98, 1)" // WARM ROOD #EA8162
              : buildingId === topBuildings[1]
                ? "rgba(91, 115, 4, 1)" // OLIJFGROEN #5B7304
                : buildingId === topBuildings[2]
                  ? "rgba(245, 221, 127, 1)" // ZACHT GEEL #F5DD7F
                  : buildingId === topBuildings[3]
                    ? "rgba(184, 237, 217, 1)" // MUNTGROEN #B8EDD9
                    : buildingId === topBuildings[4]
                      ? "rgba(40, 63, 17, 1)" // DONKERGROEN #283F11
                      : "rgba(49, 64, 59, 1)", // GRAFIET #31403B
          borderWidth: 1,
          categoryProjects, // Add project details for tooltips
        };
      });

      return {
        labels: sortedCategories.map(([category]) => category),
        datasets,
      };
    }, [projects]);

    // Subcategory-centric view data (calculated from filtered projects)
    const subcategoryChartData = useMemo(() => {
      // Initialize data structure for subcategories (RenovationLabels)
      const subcategoryData: {
        [labelId: string]: {
          name: string;
          type: RenovationLabelType;
          total: number;
          buildings: {
            [buildingId: string]: number;
          };
        };
      } = {};

      // First, collect all labels from parts that match the filters
      projects.forEach((project) => {
        const buildingId = project.building.id as string; // Type assertion

        project.parts?.forEach((part) => {
          if (!part.label) return;

          // If label type filter is applied, skip parts that don't match
          if (filters.labelType && part.label.type !== filters.labelType)
            return;

          // If label ID filter is applied, skip parts that don't match
          if (
            filters.labelIds.length > 0 &&
            !filters.labelIds.includes(part.label.id)
          )
            return;

          // Get invoiced amount for the part
          const invoicedAmount =
            invoicedAmountForPart(part);

          if (invoicedAmount <= 0) return;

          const labelId = part.label.id.toString();

          // Initialize label if not exists
          if (!subcategoryData[labelId]) {
            subcategoryData[labelId] = {
              name: part.label.name,
              type: part.label.type,
              total: 0,
              buildings: {},
            };
          }

          // Add to label total
          subcategoryData[labelId].total += invoicedAmount;

          // Initialize building if not exists - Fix type errors
          if (!subcategoryData[labelId].buildings[buildingId as string]) {
            subcategoryData[labelId].buildings[buildingId as string] = 0;
          }

          // Add to building within label - Fix type errors
          subcategoryData[labelId].buildings[buildingId as string] +=
            invoicedAmount;
        });
      });

      // Sort subcategories by total amount (descending)
      const sortedSubcategories = Object.entries(subcategoryData)
        .sort(([, a], [, b]) => b.total - a.total)
        .slice(0, 15); // Limit to top 15 subcategories to keep chart readable

      // Get top buildings by total amount (descending)
      const buildingTotals: { [buildingId: string]: number } = {};

      // Calculate total amount per building
      sortedSubcategories.forEach(([, labelData]) => {
        Object.entries(labelData.buildings).forEach(([buildingId, amount]) => {
          if (!buildingTotals[buildingId]) {
            buildingTotals[buildingId] = 0;
          }
          buildingTotals[buildingId] += amount;
        });
      });

      // Sort buildings by total amount and take top N
      const topBuildings = Object.entries(buildingTotals)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10) // Limit to top 10 buildings
        .map(([buildingId]) => buildingId);

      // Create datasets - one per building
      const datasets = topBuildings.map((buildingId) => {
        // Prepare project details for tooltips
        const subcategoryProjects = sortedSubcategories.map(([labelId]) => {
          // Collect projects for this building and subcategory
          const buildingSubcategoryProjects = projects
            .filter((project) => project.building.id === buildingId)
            .map((project) => {
              // Calculate amount from parts matching this subcategory AND the filters
              const amount =
                project.parts?.reduce((sum, part) => {
                  if (!part.label || part.label.id.toString() !== labelId)
                    return sum;

                  // Apply label type filter
                  if (
                    filters.labelType &&
                    part.label.type !== filters.labelType
                  )
                    return sum;

                  // Apply label ID filter
                  if (
                    filters.labelIds.length > 0 &&
                    !filters.labelIds.includes(part.label.id)
                  )
                    return sum;

                  const partAmount =
                    invoicedAmountForPart(part);

                  return sum + partAmount;
                }, 0) || 0;

              return {
                id: project.id,
                title: project.title || `RENO-${project.id}`,
                amount,
              };
            })
            .filter((p) => p.amount > 0)
            .sort((a, b) => b.amount - a.amount);

          return buildingSubcategoryProjects;
        });

        return {
          label: buildingId,
          data: sortedSubcategories.map(
            ([labelId]) => subcategoryData[labelId].buildings[buildingId] || 0,
          ),
          backgroundColor:
            buildingId === topBuildings[0]
              ? "rgba(234, 129, 98, 0.8)" // WARM ROOD #EA8162
              : buildingId === topBuildings[1]
                ? "rgba(91, 115, 4, 0.8)" // OLIJFGROEN #5B7304
                : buildingId === topBuildings[2]
                  ? "rgba(245, 221, 127, 0.8)" // ZACHT GEEL #F5DD7F
                  : buildingId === topBuildings[3]
                    ? "rgba(184, 237, 217, 0.8)" // MUNTGROEN #B8EDD9
                    : buildingId === topBuildings[4]
                      ? "rgba(40, 63, 17, 0.8)" // DONKERGROEN #283F11
                      : "rgba(49, 64, 59, 0.7)", // GRAFIET #31403B with decreasing opacity
          borderColor:
            buildingId === topBuildings[0]
              ? "rgba(234, 129, 98, 1)" // WARM ROOD #EA8162
              : buildingId === topBuildings[1]
                ? "rgba(91, 115, 4, 1)" // OLIJFGROEN #5B7304
                : buildingId === topBuildings[2]
                  ? "rgba(245, 221, 127, 1)" // ZACHT GEEL #F5DD7F
                  : buildingId === topBuildings[3]
                    ? "rgba(184, 237, 217, 1)" // MUNTGROEN #B8EDD9
                    : buildingId === topBuildings[4]
                      ? "rgba(40, 63, 17, 1)" // DONKERGROEN #283F11
                      : "rgba(49, 64, 59, 1)", // GRAFIET #31403B
          borderWidth: 1,
          subcategoryProjects, // Add project details for tooltips
        };
      });

      return {
        labels: sortedSubcategories.map(([, label]) => label.name),
        datasets,
      };
    }, [projects]);

    // Fix chart options errors - Only define once and fix type issues
    const buildingOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context: any) {
              return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
            },
            afterLabel: function (context: any) {
              const buildingProjects =
                context.dataset.buildingProjects[context.dataIndex];
              if (!buildingProjects || buildingProjects.length === 0) return [];

              return buildingProjects.map(
                (project: any) =>
                  `  - RENO-${project.id}: ${project.title} (${formatCurrency(project.amount)})`,
              );
            },
            footer: function (tooltipItems: any) {
              const total = tooltipItems.reduce(
                (sum: number, item: any) => sum + item.raw,
                0,
              );
              return `Totaal: ${formatCurrency(total)}`;
            },
          },
        },
        legend: {
          position: "top" as const,
          labels: {
            color: "#31403B", // GRAFIET
            font: {
              weight: "bold" as const, // Use const assertion for the font weight
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: "#31403B", // GRAFIET
          },
          grid: {
            color: "rgba(49, 64, 59, 0.1)", // GRAFIET with low opacity
          },
        },
        y: {
          stacked: true,
          ticks: {
            // Fix the type error by using the correct callback signature
            callback: function (value: number | string): string {
              return formatCurrency(Number(value));
            },
            color: "#31403B", // GRAFIET
          },
          grid: {
            color: "rgba(49, 64, 59, 0.1)", // GRAFIET with low opacity
          },
        },
      },
    };

    // Options for category-centric view
    const categoryOptions = {
      ...buildingOptions,
      plugins: {
        ...buildingOptions.plugins,
        tooltip: {
          callbacks: {
            label: function (context: any) {
              return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
            },
            afterLabel: function (context: any) {
              const projects =
                context.dataset.categoryProjects?.[context.dataIndex];
              if (!projects || projects.length === 0) return [];

              return projects.map(
                (project: any) =>
                  `  - RENO-${project.id}: ${project.title} (${formatCurrency(project.amount)})`,
              );
            },
            footer: function (tooltipItems: any) {
              const total = tooltipItems.reduce(
                (sum: number, item: any) => sum + item.raw,
                0,
              );
              return `Totaal: ${formatCurrency(total)}`;
            },
          },
        },
      },
    };

    // Options for subcategory-centric view
    const subcategoryOptions = {
      ...buildingOptions,
      plugins: {
        ...buildingOptions.plugins,
        tooltip: {
          callbacks: {
            label: function (context: any) {
              return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
            },
            afterLabel: function (context: any) {
              const projects =
                context.dataset.subcategoryProjects?.[context.dataIndex];
              if (!projects || projects.length === 0) return [];

              return projects.map(
                (project: any) =>
                  `  - RENO-${project.id}: ${project.title} (${formatCurrency(project.amount)})`,
              );
            },
            footer: function (tooltipItems: any) {
              const total = tooltipItems.reduce(
                (sum: number, item: any) => sum + item.raw,
                0,
              );
              return `Totaal: ${formatCurrency(total)}`;
            },
          },
        },
      },
    };

    // Check if any data is available
    const hasData = useMemo(() => {
      if (chartView === "byBuilding") {
        return (
          buildingChartData?.datasets?.some((d) =>
            d?.data?.some((value) => value > 0),
          ) ?? false
        );
      } else if (chartView === "byCategory") {
        return (
          categoryChartData?.datasets?.some((d) =>
            d?.data?.some((value) => value > 0),
          ) ?? false
        );
      } else {
        return (
          subcategoryChartData?.datasets?.some((d) =>
            d?.data?.some((value) => value > 0),
          ) ?? false
        );
      }
    }, [chartView, buildingChartData, categoryChartData, subcategoryChartData]);

    return (
      <Card className="p-4">
        <CardHeader>
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-neutral-primary text-lg font-medium">
                {chartView === "byBuilding"
                  ? "Gefactureerd per gebouw"
                  : chartView === "byCategory"
                    ? "Gefactureerd per renovatie categorie"
                    : "Gefactureerd per subcategorie"}
                {filters.year !== "all" && (
                  <span className="ml-2 text-sm font-normal text-neutral-500">
                    ({filters.year})
                  </span>
                )}
              </h3>
              {renderFilterChips()}
            </div>

            {/* Enhanced toggle buttons */}
            <div className="flex items-center overflow-hidden rounded-lg border border-neutral-200">
              <button
                className={`px-3 py-2 text-sm transition-colors ${
                  chartView === "byBuilding"
                    ? "bg-moss-primary font-medium text-white"
                    : "bg-white text-neutral-500 hover:bg-neutral-100"
                }`}
                onClick={() => setChartView("byBuilding")}
              >
                Per gebouw
              </button>
              <button
                className={`px-3 py-2 text-sm transition-colors ${
                  chartView === "byCategory"
                    ? "bg-moss-primary font-medium text-white"
                    : "bg-white text-neutral-500 hover:bg-neutral-100"
                }`}
                onClick={() => setChartView("byCategory")}
              >
                Per categorie
              </button>
              <button
                className={`px-3 py-2 text-sm transition-colors ${
                  chartView === "bySubcategory"
                    ? "bg-moss-primary font-medium text-white"
                    : "bg-white text-neutral-500 hover:bg-neutral-100"
                }`}
                onClick={() => setChartView("bySubcategory")}
              >
                Per subcategorie
              </button>
            </div>
          </div>
        </CardHeader>
        <div className="relative h-[400px]">
          {hasData ? (
            <Bar
              data={
                chartView === "byBuilding"
                  ? buildingChartData
                  : chartView === "byCategory"
                    ? categoryChartData
                    : subcategoryChartData
              }
              options={
                chartView === "byBuilding"
                  ? buildingOptions
                  : chartView === "byCategory"
                    ? categoryOptions
                    : subcategoryOptions
              }
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-neutral-500">
                Geen data beschikbaar voor de geselecteerde filters
              </p>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Renovatie | Yally</title>
        </Helmet>
      </HelmetProvider>

      {/* Adjust sticky position and make background solid */}
      <div className="sticky top-[80px] z-20 border-b border-neutral-200 bg-white p-4 shadow-sm">
        {/* Main filters row */}
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Select
            label="Jaar"
            selectedKeys={[filters.year.toString()]}
            onChange={(e) =>
              handleFilterChange({
                ...filters,
                year:
                  e.target.value === "all"
                    ? "all"
                    : isNaN(Number(e.target.value))
                      ? e.target.value
                      : Number(e.target.value),
              })
            }
            className="w-40"
            isDisabled={isFilterLoading}
          >
            {years.map((year) => (
              <SelectItem key={year.key}>{year.label}</SelectItem>
            ))}
          </Select>

          <Select
            label="Status"
            selectedKeys={new Set(filters.status || [])}
            onSelectionChange={(selectedKeys) => {
              const selectedValues = Array.from(selectedKeys);
              handleFilterChange({
                ...filters,
                status: selectedValues.includes("all")
                  ? undefined
                  : (selectedValues as ProjectStatus[]),
              });
            }}
            className="w-40"
            selectionMode="multiple"
          >
            <SelectItem key="all">Alle statussen</SelectItem>
            {
              Object.values(ProjectStatus).map((status) => (
                <SelectItem key={status}>
                  {getStatusDisplayLabel(status as ProjectStatus)}
                </SelectItem>
              )) as any
            }
          </Select>

          <Select
            label="Hoofdcategorie"
            selectedKeys={filters.labelType ? [filters.labelType] : ["all"]}
            onChange={(e) =>
              handleFilterChange({
                ...filters,
                labelType:
                  e.target.value === "all"
                    ? undefined
                    : (e.target.value as RenovationLabelType),
              })
            }
            className="w-40"
          >
            <SelectItem key="all">Alle hoofdcategorieën</SelectItem>
            {
              Object.values(RenovationLabelType).map((type) => (
                <SelectItem key={type}>{type}</SelectItem>
              )) as any
            }
          </Select>

          <Select
            label="Subcategorieën"
            selectedKeys={new Set(filters.labelIds.map(String))}
            onSelectionChange={(selectedKeys) => {
              const selectedValues = Array.from(selectedKeys);
              handleFilterChange({
                ...filters,
                labelIds: selectedValues.includes("all")
                  ? []
                  : selectedValues.map(Number).filter((n) => !isNaN(n)),
              });
            }}
            className="w-64"
            isDisabled={isLoadingLabels}
            selectionMode="multiple"
          >
            <SelectItem key="all">Alle subcategorieën</SelectItem>
            {
              labels
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((label) => (
                  <SelectItem key={label.id}>{label.name}</SelectItem>
                )) as any
            }
          </Select>

          <div className="min-w-[200px] flex-1">
            <SearchComponent
              resourceFilter={[ResourceType.BUILDING]}
              onSelect={(building) => {
                if (!filters.buildingIds.includes(building.resourceId)) {
                  handleFilterChange({
                    ...filters,
                    buildingIds: [...filters.buildingIds, building.resourceId],
                  });
                }
              }}
              label="Zoek gebouwen..."
            />
          </div>
        </div>

        {/* Active filters row */}
        {(filters.labelIds.length > 0 ||
          filters.buildingIds.length > 0 ||
          filters.labelType ||
          filters.status?.length) && (
          <div className="mt-4">
            <div className="mb-2 text-sm text-neutral-500">
              Toegepaste filters:
            </div>
            <div className="flex flex-wrap gap-2">
              {filters.labelIds.map((labelId) => {
                const label = labels.find((l) => l.id === labelId);
                if (!label) return null;
                return (
                  <Chip
                    key={labelId}
                    onClose={() =>
                      handleFilterChange({
                        ...filters,
                        labelIds: filters.labelIds.filter(
                          (id) => id !== labelId,
                        ),
                      })
                    }
                    className="bg-moss-primary/10"
                    isDisabled={isFilterLoading}
                  >
                    {label.name}
                  </Chip>
                );
              })}
              {filters.buildingIds.map((id) => (
                <Chip
                  key={id}
                  onClose={() =>
                    handleFilterChange({
                      ...filters,
                      buildingIds: filters.buildingIds.filter(
                        (bid) => bid !== id,
                      ),
                    })
                  }
                  className="bg-moss-primary/10"
                  isDisabled={isFilterLoading}
                >
                  {id}
                </Chip>
              ))}
              {filters.labelType && (
                <Chip
                  onClose={() =>
                    handleFilterChange({
                      ...filters,
                      labelType: undefined,
                    })
                  }
                  className="bg-moss-primary/10"
                  isDisabled={isFilterLoading}
                >
                  {filters.labelType}
                </Chip>
              )}
              {filters.status?.map((status) => (
                <Chip
                  key={status}
                  onClose={() =>
                    handleFilterChange({
                      ...filters,
                      status: filters.status?.filter((s) => s !== status),
                    })
                  }
                  className="bg-moss-primary/10"
                  isDisabled={isFilterLoading}
                >
                  {status}
                </Chip>
              ))}
              {isFilterLoading && <Spinner size="sm" />}
            </div>
          </div>
        )}
      </div>

      {/* Main content */}
      {isLoadingProjects ? (
        <div className="flex h-[calc(100vh-200px)] items-center justify-center">
          <Spinner size="lg" label="Gegevens laden..." />
        </div>
      ) : (
        (() => {
          const nowYear = new Date().getFullYear();
          const isFutureYear =
            (filters.year !== "all" && (filters.year as number) > nowYear) ||
            (typeof filters.year === "string" &&
              filters.year.includes("-future"));
          if (isFutureYear) {
            // Extract year for PlanningView - handle both numeric future years and current year future
            const yearForPlanning =
              typeof filters.year === "string" &&
              filters.year.includes("-future")
                ? nowYear
                : (filters.year as number);
            return (
              <div className="space-y-6 p-6">
                {/* Organization-wide Future Planning */}
                <div className="space-y-4">
                  <div className="mb-4 flex items-center gap-2 border-t border-neutral-200 pt-6">
                    <h2 className="text-neutral-primary text-2xl font-bold">
                      Volledige Organisatie - Toekomstplanning
                    </h2>
                  </div>
                  <PlanningView
                    projects={filteredProjects}
                    year={yearForPlanning}
                  />
                </div>
              </div>
            );
          }

          return (
            <div className="space-y-6 p-6">
              {/* Organization-wide Section */}
              <div className="space-y-4">
                <div className="mb-4 flex items-center gap-2 border-t border-neutral-200 pt-6">
                  <h2 className="text-neutral-primary text-2xl font-bold">
                    Volledige Organisatie
                  </h2>
                </div>

                {/* Metrics Cards */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {renderMetricsSection()}
                  {renderCategoryMetrics()}
                </div>

                {/* Label Charts */}
                <LabelCharts projects={filteredProjects} />

                {/* Add the supplier chart */}
                <SupplierChart projects={filteredProjects} />

                {/* Add the building chart */}
                <BuildingChart projects={filteredProjects} />

                {/* Projects Table */}
                <div ref={tableRef} className="space-y-4">
                  {renderProjectTable(filteredProjects)}
                </div>
              </div>
            </div>
          );
        })()
      )}

      {/* Add the Modal component at the end of the return statement */}
      <Modal
        isOpen={editPartModalOpen}
        onClose={() => setEditPartModalOpen(false)}
        size="md"
        classNames={{
          base: "overflow-visible!",
          wrapper: "overflow-visible!",
          body: "overflow-visible!",
          backdrop: "z-49",
        }}
      >
        <ModalContent>
          <ModalHeader>
            <h3 className="text-lg font-medium">Onderdeel bewerken</h3>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Titel"
                value={selectedPart?.title}
                onChange={(e) =>
                  setSelectedPart((prev: any) => ({
                    ...prev,
                    title: e.target.value,
                  }))
                }
                placeholder="Voer een titel in"
              />
              <div className="space-y-2">
                <label className="text-sm font-medium">Label</label>
                <div className="relative z-100">
                  <LabelSearch
                    value={selectedPart?.label}
                    onChange={(label) =>
                      setSelectedPart((prev: any) => ({ ...prev, label }))
                    }
                    placeholder="Selecteer een label"
                  />
                </div>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setEditPartModalOpen(false)}>
              Annuleren
            </Button>
            <Button
              color="primary"
              isLoading={isUpdatingPart}
              onPress={async () => {
                setIsUpdatingPart(true);
                try {
                  const response = await fetchWithAuth(
                    `${API_URL}/renovation/project/part/${selectedPart.id}`,
                    {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        title: selectedPart.title,
                        labelId: selectedPart.label?.id || null,
                      }),
                    },
                    session,
                  );

                  if (!response.ok) throw new Error("Failed to update part");

                  // Refresh the projects data
                  await fetchData(filters);
                  toast.success("Onderdeel succesvol bijgewerkt");
                  setEditPartModalOpen(false);
                } catch (error) {
                  console.error("Error updating part:", error);
                  toast.error("Fout bij bijwerken van onderdeel");
                } finally {
                  setIsUpdatingPart(false);
                }
              }}
            >
              Opslaan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}; // This semicolon should match with the Renovation component declaration

export default Renovation;
